import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export interface User {
  id?: string
  name: string
  email: string
  phone?: string
  location?: string
  jobTitle?: string
  company?: string
  bio?: string
  avatar?: string
  userType: 'contractor' | 'company' | 'admin' | 'employee' | 'company_man' | 'driver'
  isVerified?: boolean
  createdAt?: string
  lastLoginAt?: string
}

export const useUserStore = defineStore('user', () => {
  // State
  const currentUser = ref<User | null>(null)
  const isAuthenticated = ref(false)
  const authToken = ref<string | null>(null)
  const loading = ref(false)

  // Getters
  const userInitials = computed(() => {
    if (!currentUser.value?.name) return 'U'
    return currentUser.value.name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
  })

  const isContractor = computed(() => currentUser.value?.userType === 'contractor')
  const isCompany = computed(() => currentUser.value?.userType === 'company')
  const isAdmin = computed(() => currentUser.value?.userType === 'admin')

  // Actions
  const loadUserFromStorage = () => {
    const token = localStorage.getItem('auth_token')
    const userType = localStorage.getItem('user_type')
    const storedUser = localStorage.getItem('user_data')

    if (token && userType) {
      authToken.value = token
      isAuthenticated.value = true
      
      if (storedUser) {
        try {
          currentUser.value = JSON.parse(storedUser)
        } catch (error) {
          console.error('Error parsing stored user data:', error)
          // Create a basic user object from available data
          currentUser.value = {
            name: 'User',
            email: localStorage.getItem('user_email') || 'user@example.com',
            userType: userType as User['userType']
          }
        }
      } else {
        // Create a basic user object if no detailed data is stored
        currentUser.value = {
          name: 'User',
          email: localStorage.getItem('user_email') || 'user@example.com',
          userType: userType as User['userType']
        }
      }
    }
  }

  const updateUser = async (userData: Partial<User>) => {
    if (!currentUser.value) return false

    try {
      loading.value = true

      // Update the store immediately (optimistic update)
      currentUser.value = { ...currentUser.value, ...userData }

      // Save to localStorage
      localStorage.setItem('user_data', JSON.stringify(currentUser.value))
      
      // Also update specific fields in localStorage for backward compatibility
      if (userData.name) localStorage.setItem('user_name', userData.name)
      if (userData.email) localStorage.setItem('user_email', userData.email)

      return true
    } catch (error) {
      console.error('Error updating user profile:', error)
      return false
    } finally {
      loading.value = false
    }
  }

  const login = async (email: string, password: string) => {
    try {
      loading.value = true

      const response = await fetch('/api/login.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      })

      if (response.ok) {
        const data = await response.json()
        
        if (data.success) {
          localStorage.removeItem('adminToken')
          localStorage.removeItem('adminUser')
          localStorage.removeItem('is_admin')
          if (data.user.userType === 'admin') {
            data.user.userType = 'contractor'
          }
          // Store authentication data
          currentUser.value = data.user
          isAuthenticated.value = true
          authToken.value = data.token || null

          // Update localStorage
          if (data.token) localStorage.setItem('auth_token', data.token)
          localStorage.setItem('user_type', data.user.userType)
          localStorage.setItem('user_data', JSON.stringify(data.user))
          localStorage.setItem('user_name', data.user.name)
          localStorage.setItem('user_email', data.user.email)

          return { success: true }
        } else {
          return { success: false, error: data.error || 'Login failed' }
        }
      } else {
        const error = await response.json()
        return { success: false, error: error.error || 'Login failed' }
      }
    } catch (error) {
      console.error('Login error:', error)
      return { success: false, error: 'Network error occurred' }
    } finally {
      loading.value = false
    }
  }

  const logout = () => {
    // Clear store state
    currentUser.value = null
    isAuthenticated.value = false
    authToken.value = null

    // Clear localStorage
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user_type')
    localStorage.removeItem('user_data')
    localStorage.removeItem('user_name')
    localStorage.removeItem('user_email')
  }

  const fetchUserProfile = async () => {
    if (!currentUser.value?.id) return false

    try {
      loading.value = true

      const response = await fetch('/api/list-users.php', {
        method: 'POST'
      })

      if (response.ok) {
        const users = await response.json()
        const user = users.find((u: any) => u.id == currentUser.value?.id)
        if (user) {
          currentUser.value = { ...currentUser.value, ...user }
          localStorage.setItem('user_data', JSON.stringify(currentUser.value))
          return true
        }
      }
    } catch (error) {
      console.error('Error fetching user profile:', error)
    } finally {
      loading.value = false
    }

    return false
  }

  return {
    // State
    currentUser,
    isAuthenticated,
    authToken,
    loading,
    
    // Getters
    userInitials,
    isContractor,
    isCompany,
    isAdmin,
    
    // Actions
    loadUserFromStorage,
    updateUser,
    login,
    logout,
    fetchUserProfile
  }
})
